/**
 * THE REGION FIELD: which region a world position is in, and how far it is from that region's edge.
 *
 * A region is a cell of a DOMAIN-WARPED WORLEY (cellular) diagram. Everything the hierarchy above it
 * needs comes out of one lookup — a stable integer id to seed the region's contents with, the cell's
 * feature point to lay a layout out from, the region's type, and a distance-like measure of how close
 * the query is to the boundary.
 *
 * WHY THE SEARCH IS WRITTEN OUT HERE RATHER THAN CALLING FastNoiseLite's Cellular. FNL runs this exact
 * 3x3 search internally and then returns ONE float (the cell's hash), throwing away the cell index, the
 * feature point and the second-nearest distance. The pathway system uses it that way and pays for it:
 * it has no cell identity at all, and it recovers boundaries by sampling four cardinal neighbours and
 * comparing hashes — five warped noise evaluations per column for a band whose width varies with the
 * boundary's orientation. Doing the search inline is the same ~9 integer hashes and answers all four
 * questions at once.
 *
 * THE JITTER IS CLAMPED BELOW HALF A CELL, and that is a correctness property, not a taste one: a
 * feature point that can leave its own cell can be nearer to a query than anything in the query's 3x3
 * neighbourhood, and the search would silently return the wrong cell.
 *
 * THE WARP IS APPLIED TO THE QUERY, NOT THE LATTICE, so the search stays EXACT — the warp only makes
 * boundaries wiggle. Two consequences to hold on to: a region is no longer convex in world space (any
 * "is this whole chunk in one region" test must be grown by the warp amplitude), and `edge` is measured
 * in WARPED metres — which is why `RegionSample.dist` divides `edge` by its WORLD-space gradient,
 * picking the warp's Jacobian up on the way, instead of correcting for the warp with a global factor.
 */
import FastNoiseLite from 'fastnoise-lite';
import { mat } from '../../materials/index.js';
import { cityGrid, gridU, gridV, roadDrawable, roadTouches } from './CityLayout.js';
import { hashInt2 } from '../../util/rng.js';
import { VOXEL_SCALE } from '../../voxel/constants.js';
import { REACH_VERTICAL, flooredHalfWidth } from '../../voxel/surfaceBand.js';
import { packPairKey } from '../../util/packedKey.js';
/**
 * What a region IS. Nothing here generates content yet — M0 draws boundary walls only, and the type
 * exists so the walls are already colour-coded by the region they enclose. The tint is what makes the
 * next milestone (per-region scatter) readable the day it lands rather than a fortnight later.
 */
export var RegionType;
(function (RegionType) {
    RegionType["WILD"] = "wild";
    RegionType["MEADOW"] = "meadow";
    RegionType["CITY"] = "city";
})(RegionType || (RegionType = {}));
/** Finite-difference step for the warp Jacobian, in metres — one voxel, so it sits on the lattice the
 *  field is sampled on and stays far below any usable warp wavelength. */
const WARP_DIFF_STEP = 0.25;
/** Below this gradient magnitude the first-order distance is meaningless (a triple junction). */
const GRAD_EPSILON = 1e-4;
/**
 * Stencil per axis for a coarse cell (see wallCellDistance), floor and cap. Both are MEASURED against the
 * no-gaps case rather than chosen: over ~13k walled fine columns, a 2x2 floor left one lod-2 cell
 * uncovered and 3x3 left none, at either end of the settings range. The cap bounds the worst case at
 * 16 samples, and only for cells the broad phase could not already prove clear.
 */
const MIN_STENCIL = 3;
const MAX_STENCIL = 4;
/**
 * Slack on each stencil sample's reach. `dist` is a FIRST-ORDER distance, so extrapolating it to a
 * sub-cell's corner is exact only for a straight boundary; a curved one needs a little margin, and
 * without it the bound is tight enough that equality cases fail outright (a fine column sitting
 * exactly at its cell's reach). Sized by measurement — see regionWallLod's no-gaps case. It is
 * applied to the SUB-cell reach, which the stencil has already made small, so it costs little
 * coverage: at the default settings, lod-5 coverage moves 15% -> 16%.
 */
const STENCIL_SAFETY = 1.5;
/** Region types in their weight order — index is what the type hash selects. */
const TYPE_ORDER = [RegionType.MEADOW, RegionType.WILD, RegionType.CITY];
export const DEFAULT_REGION_CONFIG = {
    enabled: false,
    cellSize: 200,
    jitter: 0.5, // the clamp itself: feature points may reach their own cell's edge
    edgeDetail: 2, // two wiggles across a region -> a 100 m wavelength at the default cell
    edgeWander: 0.2, // displacement a fifth of that -> 20 m of meander
    // Written as percentages because that is what the catalog offers and what the dialog shows; they
    // are WEIGHTS, so scaling all three by ten is a no-op (the constructor normalises by their sum)
    // and they need not add to 100 once one is dragged.
    typeWeights: [50, 30, 20],
    wallMaterials: [mat('stone'), mat('stone2'), mat('concrete')], // meadow, wild, city
    wallWidth: 1,
    wallHeight: 40,
    scatterRegions: [RegionType.MEADOW],
    roadsEnabled: true, // inert until `enabled`, and CITY-only, so a default world is untouched
    roadSpacing: 40, // 40 m blocks — five to a 200 m region
    roadWidth: 6, // 6 m carriageway; expressible to lod 4, gone by lod 6 (CityLayout)
    buildingsEnabled: true,
    blockInset: 2, // 2 m of pavement between the carriageway and the wall line
    buildingMinHeight: 24, // 6 m — a two-storey shop
    buildingMaxHeight: 640, // 160 m — a tower that crosses twenty LOD-0 chunks
    buildingHeightBias: 3,
    downtownFalloff: 0.75,
    interiorsEnabled: true,
    storeyHeight: 3.5,
    roomWidth: 8,
    corridorWidth: 10,
    windowPitch: 4,
};
/** The city's carriageway. */
export function regionRoadMaterial() {
    return mat('asphalt');
}
/** Wall material per region type, so the honeycomb reads as the region map it is. */
export function regionWallMaterial(type, cfg = DEFAULT_REGION_CONFIG) {
    const i = TYPE_ORDER.indexOf(type);
    const m = i >= 0 ? cfg.wallMaterials?.[i] : undefined;
    return m ?? DEFAULT_REGION_CONFIG.wallMaterials[i >= 0 ? i : 1];
}
export class RegionField {
    cfg;
    warpX;
    warpZ;
    /** Warp displacement in metres — derived: `edgeWander x wavelength`. */
    warpAmp;
    /** Warp wavelength in metres — derived: `cellSize / edgeDetail`. The scale a boundary wiggles at,
     *  and therefore the scale a coarse cell has to be sampled at to see the wiggle. */
    wavelength;
    /** Lattice salts — XORed into the cell hash so a different world seed is a different DIAGRAM, not
     *  the same one translated (which is all an integer cell offset would buy). */
    saltX;
    saltZ;
    typeSaltX;
    typeSaltZ;
    typePick; // cumulative weights, normalised to 1
    scatterSet;
    /** Per-column memo, mirroring TerrainGenerator.heightCache: a vertical stack of chunks re-queries
     *  the same (worldX, worldZ) once per cy, and the coarse column clip asks again for the same grid. */
    cache = new Map();
    /** Reused by featurePointInto — the Worley search calls it nine times per sample. */
    fpScratch = new Float64Array(2);
    cacheEntries = 0;
    constructor(seed, config = {}) {
        this.cfg = { ...DEFAULT_REGION_CONFIG, ...config };
        // The two knobs are wavelength-relative (see RegionConfig.edgeDetail); the noise wants a
        // frequency and a displacement, so derive both here, once.
        const cell = this.cfg.cellSize > 0 ? this.cfg.cellSize : 1;
        const detail = this.cfg.edgeDetail > 0 ? this.cfg.edgeDetail : 1;
        const wavelength = cell / detail;
        this.wavelength = wavelength;
        this.warpAmp = Math.min(0.5, Math.max(0, this.cfg.edgeWander)) * wavelength;
        const frequency = 1 / wavelength;
        // Own seed block, off every existing chain (base seed++, +10000 stamps, +30000 caves,
        // +50000 landform, +80000 rivers) so adding regions moves nothing that already exists.
        this.warpX = new FastNoiseLite(seed + 70001);
        this.warpX.SetNoiseType(FastNoiseLite.NoiseType.OpenSimplex2);
        this.warpX.SetFractalOctaves(1); // as the pathway warp: a broad wiggle, fine detail is wasted
        this.warpX.SetFrequency(frequency);
        this.warpZ = new FastNoiseLite(seed + 70002);
        this.warpZ.SetNoiseType(FastNoiseLite.NoiseType.OpenSimplex2);
        this.warpZ.SetFractalOctaves(1);
        this.warpZ.SetFrequency(frequency);
        this.saltX = hashInt2(seed, 0x51ed270b);
        this.saltZ = hashInt2(seed, 0x2545f491);
        this.typeSaltX = hashInt2(seed, 0x9e3779b1);
        this.typeSaltZ = hashInt2(seed, 0x85ebca6b);
        const w = this.cfg.typeWeights.length === TYPE_ORDER.length ? this.cfg.typeWeights : [1, 1, 1];
        const total = w.reduce((a, b) => a + Math.max(0, b), 0) || 1;
        const cum = [];
        let acc = 0;
        for (const x of w) {
            acc += Math.max(0, x) / total;
            cum.push(acc);
        }
        this.typePick = cum;
        this.scatterSet = new Set(this.cfg.scatterRegions ?? []);
    }
    /** This field's wall colour for a type — the method form, so callers with a field never have to
     *  thread the config through themselves. */
    wallMaterial(type) {
        return regionWallMaterial(type, this.cfg);
    }
    /** Whether scattered stamps stand in this kind of region at all. */
    allowsScatter(type) {
        return this.scatterSet.has(type);
    }
    /**
     * Whether a stamp of horizontal radius `footprint` metres, standing here, would overlap a boundary
     * wall.
     *
     * Asked at the stamp's own position with the FINE wall width at every level, deliberately: the
     * coarse wall is drawn wider (its reach grows with the stride), but a gate that widened with it
     * would keep a tree at lod 0 and drop it at lod 3, which is the popping that lodPlacementParity
     * exists to prevent. The drawn wall swallowing a little more of a tree at range is invisible; the
     * tree existing at one level and not another is not.
     */
    wallOverlaps(worldX, worldZ, footprint) {
        if (this.cfg.wallWidth <= 0 || this.cfg.wallHeight <= 0)
            return false;
        return this.sample(worldX, worldZ).dist < this.cfg.wallWidth * 0.5 + footprint;
    }
    getConfig() { return this.cfg; }
    /**
     * The boundary distance at which a wall covers a point — just its half-width. Exposed because the
     * tests and the diagnostics threshold on the same number.
     */
    wallDistThreshold(cellReach) {
        return this.cfg.wallWidth * 0.5 + cellReach;
    }
    /**
     * THE MAX-DOWNSAMPLE, AS A DISTANCE: how far is the drawn wall from the lattice cell centred at
     * (cx, cz) with half-extent `halfExtent` metres? Signed metres, negative inside, +Infinity where
     * there is no wall at all. The nearest point of the CELL answers, not its centre.
     *
     * THERE IS NO SEPARATE PREDICATE. This replaced `isWallCell`, which returned the sign of exactly
     * this quantity and threw the rest away — and a boolean is what makes a wall JAGGED, because
     * SurfaceNets has nothing left to interpolate (voxel/surfaceBand.ts). A predicate that can also be
     * asked with different arguments than the drawing uses is a second way for the two to disagree.
     * Ask for the number and take its sign.
     *
     * WHY THIS EXISTS AT ALL. `generateChunkLOD` samples one point per coarse column by design (coarse
     * points are a subset of the fine lattice, which is what shares the height cache and makes
     * cross-LOD seams see coincident points). Point-sampling a heightfield is right; point-sampling a
     * 1 m wall through an 8 m lod-5 column is not — it registers only where the sample lands in the
     * band, so the wall comes out DASHED and gets worse every level. The rule has to be the one
     * voxel/fold.ts states for the same problem: strongest wins the cell.
     *
     * WHY IT IS A STENCIL AND NOT ONE WIDENED THRESHOLD. Widening was the first cut, and it is right
     * whenever the boundary is straight across the cell: `dist` is a first-order distance, so
     * `dist < halfWidth + reach` extrapolates the boundary linearly to the cell's far corner. What it
     * cannot do is see a boundary that ENTERS AND LEAVES the cell — and that is not an edge case, it is
     * plain aliasing: at `edgeDetail` 12 on a 200 m region the boundary wiggles every 16.7 m, which is
     * finer than a lod-6 cell. Measured, widening alone left 1344 walled fine columns with no walled
     * coarse cell at lod 5, and no safety factor closed them (2x still left 358) because the
     * information is not in the sample. So the stencil is sized to the WIGGLE: enough samples that
     * neighbours are within half a wavelength, which is exactly the rate at which the wiggle is
     * representable.
     *
     * IT STAYS CHEAP because of the broad phase. `dist` is a real distance, so one sample proves a cell
     * clear whenever the nearest boundary is further than the cell's own reach plus a wavelength of
     * slack — true of the ~75-98% of columns that hold no wall. Only cells near a boundary walk the
     * stencil, and it is capped at 4x4. (Perf-guide invariant 3: broad-phase before per-item work.)
     *
     * THE HALF-WIDTH IS FLOORED AT WHAT THE LATTICE CAN HOLD. The max-downsample above makes a sub-voxel
     * wall REGISTER on a coarse column; it does not make it thick. Measuring the true 1 m half-width
     * against an 8 m lod-5 voxel leaves the band peaking near weight 0.2 — a translucent sheet, which is
     * the dissolve the max rule exists to prevent, arriving through the back door. A region boundary is
     * a warped Worley edge, so its normal is horizontal at an arbitrary yaw: REACH_VERTICAL. The wall
     * survives today WITHOUT this, but only because the stencil above supercovers the cell — which is a
     * safety factor tuned for a different problem doing this one's job by accident.
     */
    wallCellDistance(cx, cz, halfExtent, strideFine) {
        if (this.cfg.wallWidth <= 0 || this.cfg.wallHeight <= 0)
            return Infinity;
        const thr0 = this.cfg.wallWidth * 0.5;
        if (!(halfExtent > 0)) { // LOD 0: the sample IS the voxel
            return this.sample(cx, cz).dist - flooredHalfWidth(thr0, strideFine, VOXEL_SCALE, REACH_VERTICAL);
        }
        const reach = halfExtent * Math.SQRT2;
        // Broad phase: provably clear, in one sample. The wavelength is the slack for a boundary that
        // bends back toward the cell after the linear extrapolation says it has left. `dist` is a real
        // distance, so subtracting the cell's reach turns that sample into a lower bound on the whole
        // cell's — still comfortably positive, which is all the sign contract requires.
        const centre = this.sample(cx, cz).dist;
        if (centre > thr0 + reach + this.wavelength)
            return centre - reach - thr0;
        const span = 2 * halfExtent;
        // AT LEAST 2x2 once there is a cell at all. Sizing the stencil to the WAVELENGTH alone leaves
        // k = 1 for any cell smaller than half a wiggle — which sounds safe and is not, because the other
        // thing a single sample cannot see is a boundary belonging to a cell that is not among the three
        // nearest AT THE SAMPLE. That is an ordinary 4-cell corner, and it left a lod-6 cell reporting
        // 22 m of clearance with a walled fine column 8.5 m inside it. Subdividing bounds how far any
        // answer is extrapolated, which is the actual failure mode; the wavelength term then refines it
        // further where the boundary also wiggles.
        const k = Math.max(MIN_STENCIL, Math.min(MAX_STENCIL, Math.ceil(span / (this.wavelength * 0.5))));
        const step = span / k;
        const subReach = step * 0.5 * Math.SQRT2 * STENCIL_SAFETY;
        let nearest = Infinity;
        for (let j = 0; j < k; j++) {
            for (let i = 0; i < k; i++) {
                const x = cx - halfExtent + (i + 0.5) * step;
                const z = cz - halfExtent + (j + 0.5) * step;
                const d = this.sample(x, z).dist;
                if (d < nearest)
                    nearest = d;
            }
        }
        return nearest - flooredHalfWidth(thr0 + subReach, strideFine, VOXEL_SCALE, REACH_VERTICAL);
    }
    /**
     * Is this cell road? CITY cells only, and never inside the boundary wall — the wall owns the band it
     * stands in, and a road drawn under it would surface either side of it as a stripe the wall happens
     * to be standing on rather than a carriageway.
     *
     * `halfExtent` is the coarse cell's half-width in metres, 0 at LOD 0. Like the wall this is a
     * MAX-downsample (any part of the cell on a road makes the cell road), which is what keeps a road
     * continuous at range; unlike the wall it switches OFF at the level where the widening would pave
     * the block over. Both halves of that live in CityLayout.
     *
     * The region type is point-sampled at the cell centre, as the wall's material is — a cell straddling
     * a boundary belongs to whichever region its centre is in.
     */
    isRoadCell(cx, cz, halfExtent) {
        const cfg = this.cfg;
        if (!cfg.roadsEnabled || cfg.roadWidth <= 0 || cfg.roadSpacing <= 0)
            return false;
        if (!roadDrawable(halfExtent, cfg.roadSpacing, cfg.roadWidth))
            return false;
        const s = this.sample(cx, cz);
        if (s.type !== RegionType.CITY)
            return false;
        if (s.dist < cfg.wallWidth * 0.5)
            return false; // the wall's own band
        const g = cityGrid(s.id, cfg.roadSpacing);
        const u = gridU(cx, cz, s.centreX, s.centreZ, g);
        const v = gridV(cx, cz, s.centreX, s.centreZ, g);
        return roadTouches(u, v, halfExtent, g, cfg.roadWidth * 0.5);
    }
    /**
     * Every lattice cell whose TERRITORY can reach a world-space box, as (ci, cj) pairs.
     *
     * Not the cells whose lattice squares overlap the box: a feature point jitters up to half a cell
     * from its lattice point and the warp displaces the boundary further still, so a cell can own
     * ground well outside its own square. The margin is that reach, rounded up — conservative, and
     * cheap because rejecting a cell is one hash (`typeAt`).
     */
    forEachCell(minX, minZ, maxX, maxZ, fn) {
        const size = this.cfg.cellSize;
        const reach = size * Math.min(0.5, this.cfg.jitter) + this.warpAmp;
        const m = Math.ceil(reach / size) + 1;
        const i0 = Math.floor(minX / size) - m, i1 = Math.floor(maxX / size) + m;
        const j0 = Math.floor(minZ / size) - m, j1 = Math.floor(maxZ / size) + m;
        for (let cj = j0; cj <= j1; cj++)
            for (let ci = i0; ci <= i1; ci++)
                fn(ci, cj);
    }
    /** The feature point of a lattice cell — the anchor a region's layout is built on. */
    cellCentre(ci, cj) {
        const out = new Float64Array(2);
        this.featurePointInto(ci, cj, out);
        return { x: out[0], z: out[1] };
    }
    /**
     * The jittered feature point of one lattice cell. ONE definition, shared with the Worley search —
     * the city grid is anchored on this, so a second copy of the formula would drift the layout away
     * from the region it is supposed to be laid out in.
     *
     * (In warped coordinates, like the search itself. That is only an offset as far as the grid is
     * concerned: the grid is built in WORLD space from it, so roads come out straight.)
     */
    featurePointInto(ci, cj, out) {
        const size = this.cfg.cellSize > 0 ? this.cfg.cellSize : 1;
        const jitter = Math.min(0.5, Math.max(0, this.cfg.jitter));
        const h = hashInt2(ci ^ this.saltX, cj ^ this.saltZ);
        // Two ~independent 16-bit fields off one FNV-mixed hash — cheaper than two hashes, and the
        // mix has already diffused the inputs across the whole word.
        const jx = ((h & 0xffff) / 0xffff) * 2 - 1;
        const jz = (((h >>> 16) & 0xffff) / 0xffff) * 2 - 1;
        out[0] = (ci + 0.5 + jx * jitter) * size;
        out[1] = (cj + 0.5 + jz * jitter) * size;
    }
    /** The region at a world position (memoised per column). */
    sample(worldX, worldZ) {
        let inner = this.cache.get(worldX);
        if (inner) {
            const hit = inner.get(worldZ);
            if (hit !== undefined)
                return hit;
        }
        const s = this.computeSample(worldX, worldZ);
        // Bounded by wholesale clear rather than LRU, as heightCache is: the working set is the columns
        // of the active view, and a clear costs one re-walk of them.
        if (this.cacheEntries > 262144) {
            this.cache.clear();
            this.cacheEntries = 0;
            inner = undefined;
        }
        if (!inner) {
            inner = new Map();
            this.cache.set(worldX, inner);
        }
        inner.set(worldZ, s);
        this.cacheEntries++;
        return s;
    }
    computeSample(worldX, worldZ) {
        const size = this.cfg.cellSize > 0 ? this.cfg.cellSize : 1;
        const amp = this.warpAmp;
        // Warp the QUERY. The lattice below is unwarped, so the 3x3 search stays exact.
        const nx0 = amp !== 0 ? this.warpX.GetNoise(worldX, worldZ) : 0;
        const nz0 = amp !== 0 ? this.warpZ.GetNoise(worldX, worldZ) : 0;
        const wx = worldX + amp * nx0;
        const wz = worldZ + amp * nz0;
        const gi = Math.floor(wx / size);
        const gj = Math.floor(wz / size);
        let d1 = Infinity, d2 = Infinity, d3 = Infinity;
        let bi = gi, bj = gj, bx = 0, bz = 0; // nearest: cell + feature point
        let ni = gi, nj = gj, nfx = 0, nfz = 0; // second-nearest: cell + feature point
        let tfx = 0, tfz = 0; // third-nearest feature point (see `dist` below)
        for (let dj = -1; dj <= 1; dj++) {
            for (let di = -1; di <= 1; di++) {
                const ci = gi + di, cj = gj + dj;
                this.featurePointInto(ci, cj, this.fpScratch);
                const fx = this.fpScratch[0], fz = this.fpScratch[1];
                const ddx = wx - fx, ddz = wz - fz;
                const d = Math.sqrt(ddx * ddx + ddz * ddz);
                if (d < d1) {
                    d3 = d2;
                    tfx = nfx;
                    tfz = nfz;
                    d2 = d1;
                    ni = bi;
                    nj = bj;
                    nfx = bx;
                    nfz = bz;
                    d1 = d;
                    bi = ci;
                    bj = cj;
                    bx = fx;
                    bz = fz;
                }
                else if (d < d2) {
                    d3 = d2;
                    tfx = nfx;
                    tfz = nfz;
                    d2 = d;
                    ni = ci;
                    nj = cj;
                    nfx = fx;
                    nfz = fz;
                }
                else if (d < d3) {
                    d3 = d;
                    tfx = fx;
                    tfz = fz;
                }
            }
        }
        // THE GRADIENT, AND WHY IT IS NEARLY FREE.
        //
        // grad(dk - d1) in WARPED space is `u_k - u_1`, the unit vectors from the feature points to the
        // query — already in hand, no extra sampling. The world-space gradient is that pushed back through
        // the warp: grad_world = J^T (u_k - u_1), where J = d(warped)/d(world) = I + amp * grad(noise). So
        // only the NOISE needs differencing: four evaluations, and none at all when the warp is off.
        //
        // Differencing the field itself would have cost two whole extra samples AND been worse, because
        // it has kinks wherever the nearest or second-nearest cell changes — precisely the corners where
        // the estimate matters most.
        let jxx = 1, jzx = 0, jxz = 0, jzz = 1;
        if (amp !== 0) {
            const h = WARP_DIFF_STEP;
            jxx = 1 + amp * (this.warpX.GetNoise(worldX + h, worldZ) - nx0) / h;
            jzx = amp * (this.warpZ.GetNoise(worldX + h, worldZ) - nz0) / h;
            jxz = amp * (this.warpX.GetNoise(worldX, worldZ + h) - nx0) / h;
            jzz = 1 + amp * (this.warpZ.GetNoise(worldX, worldZ + h) - nz0) / h;
        }
        /** World distance to the boundary between the nearest cell and the one at (fx, fz). */
        const pairDist = (dk, fx, fz) => {
            if (!(dk > 0) || !isFinite(dk) || !(d1 > 0))
                return Infinity;
            const gwx = (wx - fx) / dk - (wx - bx) / d1;
            const gwz = (wz - fz) / dk - (wz - bz) / d1;
            const gx = jxx * gwx + jzx * gwz; // J^T row x
            const gz = jxz * gwx + jzz * gwz; // J^T row z
            const m = Math.hypot(gx, gz);
            return m > GRAD_EPSILON ? (dk - d1) / m : Infinity;
        };
        // BOTH BOUNDARIES, NOT JUST THE NEAREST PAIR — and this is what makes a triple junction behave.
        // `d2 - d1` alone describes ONE boundary, and at a junction that pair degenerates: its gradient
        // goes to zero, the ratio blows up, and the point reports itself far from any wall — which is a
        // HOLE at exactly the place three walls meet, and it is what the coarse no-gaps case caught.
        // The 1-3 pair is a different boundary through the same point and is well conditioned there (the
        // three gradients sit ~120 deg apart), so the min of the two is finite and small at a junction,
        // large in a cell's interior, and equal to the 1-2 answer everywhere else.
        const edge = d2 - d1;
        const dist = Math.min(pairDist(d2, nfx, nfz), pairDist(d3, tfx, tfz));
        return {
            id: packPairKey(bi, bj),
            ci: bi,
            cj: bj,
            type: this.typeAt(bi, bj),
            centreX: bx,
            centreZ: bz,
            d1,
            d2,
            edge,
            dist,
            neighbourId: packPairKey(ni, nj),
        };
    }
    /** The type of a cell. Its own salts, so the type roll is independent of the feature-point jitter —
     *  otherwise cells would systematically pair a type with a jitter direction. */
    typeAt(ci, cj) {
        const u = hashInt2(ci ^ this.typeSaltX, cj ^ this.typeSaltZ) / 0x100000000;
        for (let i = 0; i < this.typePick.length; i++) {
            if (u < this.typePick[i])
                return TYPE_ORDER[i];
        }
        return TYPE_ORDER[TYPE_ORDER.length - 1];
    }
}
//# sourceMappingURL=RegionField.js.map